import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  MenuItem,
  Typography,
  Select,
  Box,
  CircularProgress,
} from "@mui/material";
import { RxCross2 } from "react-icons/rx";
import ActiveUserIcon from "@assets/icons/activeUserIcon";
import InactiveUserIcon from "@assets/icons/inactiveUserIcon";

interface Organization {
  id: number;
  hospitalId: string;
  hospitalName: string;
  zendeskOrgId: number;
}

interface RenderUsersListProps {
  users: any[];
  securityGroups: string[];
  statuses: string[];
  handleUserNavigation: (userId:number) => void;
  handleSort: (property: string) => void;
  order: "desc" | "asc";
  orderBy: string;
  handleSecurityGroup: (
    event: React.ChangeEvent<HTMLInputElement | { value: string; name: string }>
  ) => void;
  handleStatus: (
    event: React.ChangeEvent<HTMLInputElement | { value: string; name: string }>
  ) => void;
  selectedSecurityGroup: string;
  organizations: Organization[];
  clearFilter: (value: string) => void;
  loading: boolean;
}

const RenderUsersList: React.FC<RenderUsersListProps> = ({
  users,
  securityGroups,
  statuses,
  handleUserNavigation,
  handleSort,
  order,
  orderBy,
  handleSecurityGroup,
  handleStatus,
  selectedSecurityGroup,
  organizations,
  clearFilter,
  loading,
}) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [showBorder, setShowBorder] = useState(false);

useEffect(() => {
  const container = tableContainerRef.current;
  if (!container) return;
  const checkScroll = () => {
    const scrollPresent = container.scrollWidth > container.clientWidth;
    setShowBorder(scrollPresent);
  };
  checkScroll();
  window.addEventListener("resize", checkScroll);
  return () => window.removeEventListener("resize", checkScroll);
}, []); 

  return (
    <TableContainer
      sx={{
        height: { xs: "calc(100vh - 392px)", sm: "calc(100vh - 323px)" },
        overflowY: "auto",
      }}
      ref={tableContainerRef}
    >
      <Table>
        <TableHead>
          <TableRow sx={{ padding: "0 16px" }}>
            <TableCell className={showBorder ? "border-r-4": "border-none"} sx={{ borderBottom: 0, position:"sticky", left:"0", zIndex:1, background:"white", fontWeight:"600",color:"#515C66"}}>
              <TableSortLabel
                sx={{ '&.Mui-active': {
      color: '#515C66',
    },width: "max-content" }}
                active={true}
                direction={orderBy === "firstName" ? order : "desc"}
                onClick={() => handleSort("firstName")}
              >
                First Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ borderBottom: 0, fontWeight:"600",color:"#515C66"  }}>
              <TableSortLabel
                sx={{ width: "max-content",'&.Mui-active': {
      color: '#515C66',
    } }}
                active={true}
                direction={orderBy === "lastName" ? order : "desc"}
                onClick={() => handleSort("lastName")}
              >
                Last Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ borderBottom: 0, fontWeight:"600",color:"#515C66"  }}>Organization</TableCell>
            <TableCell sx={{ borderBottom: 0, fontWeight:"600" ,color:"#515C66" }}>
              <TableSortLabel
                sx={{ width: "max-content",'&.Mui-active': {
      color: '#515C66',
    }  }}
                active={true}
                direction={orderBy === "emailId" ? order : "desc"}
                onClick={() => handleSort("emailId")}
              >
                Email ID
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ borderBottom: 0 }}>
              <Select
                onChange={handleSecurityGroup}
                fullWidth
                defaultValue=""
                MenuProps={{PaperProps: {style: {maxHeight: 250, overflowY: 'auto'}}}}
                displayEmpty
                sx={{
                  width: "inherit",
                  "& .MuiTypography-root":{
                   fontSize: "14px"
                  },
                  "& .MuiSelect-outlined": {
                    padding: "4px 0 4px 12px",
                  },color:"#515C66" 
                  
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return <Typography sx={{fontWeight:"600"}}>Security Group</Typography>;
                  }
                  const group = securityGroups.find(
                    (group: any) => group.id === selectedSecurityGroup
                  );
                  return group ? group.groupName : selected;
                }}
              >
                {securityGroups?.map((group: any) => (
                  <MenuItem key={group.id} value={group.id}>{group.groupName}</MenuItem>
                ))}
                <MenuItem onClick={() => clearFilter("securityGroup")}>
                  <RxCross2 /> Clear
                </MenuItem>
              </Select>
            </TableCell>
            <TableCell sx={{ borderBottom: 0 }}>
              <Select
                onChange={handleStatus}
                fullWidth
                displayEmpty
                sx={{
                  width: "inherit",
                  "& .MuiTypography-root":{
                    fontSize: "14px"
                   },
                  "& .MuiSelect-outlined": {
                    padding: "4px 0 4px 12px",
                  },color:"#515C66" 
                  
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return <Typography sx={{fontWeight:"600"}}>Status</Typography>;
                  }
                  return selected;
                }}
              >
                {statuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
                <MenuItem onClick={() => clearFilter("status")}>
                  <RxCross2 /> Clear
                </MenuItem>
              </Select>
            </TableCell>
          </TableRow>
        </TableHead>
        {(loading || users.length===0) ? (
          <Box
            sx={{
              height: {
                xs: "calc(100vh - 412px)",
                sm: "calc(100vh - 365px)",
              },
              position: "relative",
              left: "250%",
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily:"Graphik, sans-serif" 
            }}
          >
            {" "}
            {loading ? <CircularProgress size="20px"  /> : "No Users Found"}
          </Box>
        ) : (
          <TableBody>
            {users.map((user, index) => (
              <TableRow
                onClick={()=>handleUserNavigation(user.userId)}
                key={user.id}
                sx={{ backgroundColor: index % 2 === 0 ? "#F9FAFC" : "fff", cursor:"pointer"}}
              >
                <TableCell className={`tbl-cell ${showBorder} ? "border-r-4": "border-none"`} sx={{ borderBottom: 0, position:"sticky", left:"0", zIndex:1, background: index % 2 === 0 ? "#F9FAFC" : "white"}}>{user.firstName ? user.firstName : ""}</TableCell>
                <TableCell className="tbl-cell" sx={{ borderBottom: 0 }}>{user.lastName ? user.lastName : ""}</TableCell>
                {organizations &&
                  (() => {
                    if (organizations.hospitalId === user.hospitalId) {
                      return (
                        <TableCell className="tbl-cell" sx={{ borderBottom: 0 }}>
                          {organizations.hospitalName}
                        </TableCell>
                      );
                    }
                    return(
                      <TableCell className="tbl-cell" sx={{ borderBottom: 0 }}>
                      {""}
                    </TableCell>
                    );
                  })()}

                <TableCell className="tbl-cell" sx={{ borderBottom: 0 }}>{user.emailID ? user.emailID : ""}</TableCell>
                {securityGroups &&
                  (() => {
                    const matchSecurityGroup = securityGroups.find(
                      (group) => group.id === user.securityGroupId
                    );
                    if (matchSecurityGroup) {
                      return (
                        <TableCell className="tbl-cell" sx={{ borderBottom: 0, paddingLeft:"1.2rem" }}>
                          {matchSecurityGroup.groupName}
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell className="tbl-cell" sx={{ borderBottom: 0, paddingLeft:"1.2rem" }}>
                      {""}
                    </TableCell>
                    );
                  })()}
                <TableCell className="tbl-cell" sx={{ borderBottom: 0, paddingLeft:"1.2rem"}}>
                  {user.isActive ?
                  <Typography className="flex flex-col items-center gap-1 md:!flex md:!flex-row md:!gap-2 md:!items-center"><ActiveUserIcon/> Active </Typography>
                    : <Typography className="flex flex-col items-center gap-1 md:!flex md:!flex-row md:!gap-2 md:!items-center"><InactiveUserIcon/> Inactive </Typography>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        )}
      </Table>
    </TableContainer>
  );
};

export default RenderUsersList;
